<template>
  <div class="max-w-144 h-full">
    <div
      v-if="inModal"
      ref="Close"
      role="button"
      aria-label="Chiudi"
      class="ucrs-modal__close ucrs-modal__close--absolute ucrs-modal__close--black"
      tabindex="0"
      @click="close"
      @keyup.enter="close"
    />
    <h1 class="text-center text-3xl font-bold mb-14 md:mb-16">
      {{ title }}
    </h1>
    <slot name="subtitle">
      <div class="mb-10">
        <p class="mb-4">
          In UniCredit RE Services la protezione dei tuoi dati è fondamentale.
          <strong>Per garantire la sicurezza del tuo account abbiamo rafforzato i
          requisiti della tua password.</strong>
        </p>
        <p>Accedi ora per saperne di più.</p>
      </div>
    </slot>
    <form @submit.prevent>
      <ValidationObserver
        v-slot="{ errors }"
        ref="ValidationObserver"
        :slim="true"
      >
        <InputWithValidation
          :value="email"
          name="email"
          label="Email"
          placeholder="Inserisci la tua email"
          rules="required|email"
          :errors="errors"
          :field-classes="errors.email?.length ? 'mb-20' : 'mb-12'"
          @input="email = $event"
        />
        <InputWithValidation
          :value="password"
          type="password"
          password-reveal
          name="password"
          label="Password"
          placeholder="Inserisci la tua password"
          rules="required"
          :errors="errors"
          :field-classes="errors.password?.length ? 'mb-20' : 'mb-8'"
          @input="password = $event"
        />
        <button
          class="flex items-center text-sm font-bold uppercase text-blue outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue"
          type="button"
          @click="onPasswordRecoveryClick"
        >
          Password dimenticata?
          <SvgIcon name="angle-right-blue" class="h-5 w-5 ml-6" />
        </button>
      </ValidationObserver>
      <div class="text-center my-18">
        <p class="text-red mb-4" v-text="error"></p>
        <UcrsButton class="w-full" type="primary" @click.native.prevent="login">
          Accedi
        </UcrsButton>
      </div>
    </form>
    <p v-if="showRegister" class="flex justify-between items-center">
      <span class="text-black-light font-bold"> Sei un nuovo utente? </span>
      <UcrsButton ref="Register" type="secondary" @click="onRegisterClick">
        Registrati qui
      </UcrsButton>
    </p>
  </div>
</template>

<script>
// Components
import UcrsButton from '~/components/UcrsButton.vue'
import InputWithValidation from '~/components/InputWithValidation.vue'

export default {
  components: {
    UcrsButton,
    InputWithValidation,
  },

  props: {
    inModal: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: "Accedi all'area riservata",
    },

    showRegister: {
      type: Boolean,
      default: true,
    },

    closeOnSuccess: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      email: null,
      password: null,
      error: null,
    }
  },

  mounted() {
    if (this.inModal) {
      this.$refs.Close.focus()
      document.addEventListener('keydown', this.handleFocus)
    }
    const savedSearch = this.$store.state.interactions.search

    if (!savedSearch) {
      return
    }

    const { email } = savedSearch
    this.email = email
  },

  destroyed() {
    if (this.inModal) {
      document.removeEventListener('keydown', this.handleFocus)
    }
  },

  methods: {
    handleFocus(e) {
      this.$utilities.handleModalFocus(
        e,
        this.$refs.Close,
        this.$refs.Register.$el,
        this.$el
      )
    },

    onRegisterClick() {
      this.$router.push('/area-riservata/registrati')
      this.close()
    },

    onPasswordRecoveryClick() {
      this.$router.push('/area-riservata/reset')
      this.close()
    },

    async login() {
      const { ValidationObserver } = this.$refs

      await ValidationObserver.validate()
      const valid = ValidationObserver.flags.valid

      if (!valid) return

      const { email, password } = this

      try {
        // 1. CSRF (Fondamentale per i Cookie)
        try {
          await this.$axios.get('/_api/sanctum/csrf-cookie')
        } catch (e) {
          console.error('CSRF error', e)
        }

        // 2. Login: Laravel ora risponderà settando il Cookie HttpOnly
        // NON usare loginWith qui se vuoi gestire manualmente il flusso OTP "Limbo"
        const response = await this.$axios.post('/_api/api/v3/auth/login', {
          email,
          password,
        })

        const {
          requires_otp: requiresOtp,
          password_expired: passwordExpired,
        } = response.data

        // --- PUNTO CRUCIALE: RIMOSSO TUTTO IL BLOCCO TOKEN ---
        // Non facciamo più setToken o strategy.token.set.
        // Il browser ha ricevuto il cookie 'unicredit_session' e lo userà da solo.
        // -----------------------------------------------------

          // 3. Gestione OTP (Limbo State)
        if (requiresOtp) {
          this.$store.commit('interactions/setPendingEmail', email)
          this.$auth.strategy.token.set('true')

          if (passwordExpired) {
            this.$store.commit('interactions/setPasswordExpired', true)
          }

          this.close()
          await this.$router.push('/area-riservata/otp-verify')
          return
        }

        // 4. Gestione Password Scaduta
        if (passwordExpired) {
          // Salviamo lo stato di password scaduta nello store
          this.$store.commit('interactions/setPasswordExpired', true)

          // Salviamo anche la mail per eventuali visualizzazioni
          this.$store.commit('interactions/setPendingEmail', email)

          this.close()
          await this.$router.push('/area-riservata/rinnova-password')
          return
        }

        // 5. Login Diretto (Nessun OTP richiesto)
        // Se siamo qui, il middleware backend ci lascia passare.
        // Diciamo a Nuxt Auth di scaricare l'utente e segnarlo come loggato.
        const strategyOptions = this.$auth.strategy.options || {}
        const cookieOptions = strategyOptions.cookie || {}
        const innerOptions = cookieOptions.options || {}
        const maxAge =
          innerOptions.maxAge || cookieOptions.maxAge || 60 * 60 * 24 * 30

        this.$cookies.set('auth._token.laravelSanctum', 'true', {
          path: '/',
          maxAge,
          domain: this.$config.cookieDomain,
          secure: this.$config.cookieSecure,
        })

        if (!this.$auth.loggedIn) {
          await this.$auth.fetchUser()
        }

        if (this.$auth.user) {
          this.$auth.$storage.setState('loggedIn', true)
        }

        this.$tealium.track({
          event_name: 'login_ok',
          user_email: email,
          login_type: 'Sito',
        })

        this.$emit('success')

        if (this.closeOnSuccess) {
          this.$emit('close')
        }
      } catch (e) {
        if (!e.response) {
          console.error('Login Application Error:', e)
          return
        }

        const msg = e.response?.data?.message || 'Username o password errati'
        const validationErrors = e.response?.data?.errors
        if (validationErrors && validationErrors.email) {
          this.error = validationErrors.email[0]
        } else {
          this.error = msg
        }
        setTimeout(() => {
          this.error = null
        }, 8000)
      }
    },

    close() {
      this.$tealium.track({ event_name: 'login_popup_close' })
      this.$emit('close')
    },
  },
}
</script>

<style lang="postcss">
@import '~/assets/css/components/field';
@import '~/assets/css/components/input';
</style>
